/**
 * InlineCreateRow — a "＋ New" button that swaps in place for a row of text inputs with
 * Add / Cancel, so a catalog select is never a dead end when the value the operator
 * needs doesn't exist yet (spec 022 / #5, extended to Model + the editor's Details).
 *
 * Generic over the number of fields (Style needs one, a phone model needs Brand +
 * Model). Keyboard: Enter submits, Escape cancels — same contract as the Wizard's
 * original new-style row. The submit callback owns the API call and selection; a
 * thrown error is surfaced through `onError` (never swallowed) and the row stays
 * open so the input isn't lost.
 *
 * Optional `edit` prop adds an "Edit" trigger beside "New": it opens the same row
 * PREFILLED with the currently-selected entry's values and submits through
 * `edit.onSubmit` (PATCH instead of POST). Passing `edit: null`/omitting it hides
 * the trigger — e.g. while nothing is selected.
 */
import { Fragment, useState } from "react";
import { Icon } from "./Icon.js";

export interface InlineCreateField {
  key: string;
  placeholder: string;
  ariaLabel: string;
  /**
   * LB-2 — hard cap matching the server's column width. Base type is `String(30)`; SQLite
   * ignores `VARCHAR(n)` so an over-long value saved fine in dev and raised
   * `StringDataRightTruncation` → a bare 500 on production Postgres, with the whole Details
   * form unsaveable and no clue which field was at fault. The value is truncated on input
   * (not just by the DOM attribute, which a paste in some engines can outrun) and a counter
   * appears within 5 of the cap so the limit is visible BEFORE it silently eats characters.
   */
  maxLength?: number;
}

/** How close to `maxLength` the value must be before the counter appears. */
const COUNTER_WITHIN = 5;

export interface InlineCreateRowProps {
  /** aria-label for the input group + title for the trigger button. */
  label: string;
  fields: InlineCreateField[];
  /** Creates the entry (values keyed by field key, already trimmed). Throws on failure. */
  onSubmit: (values: Record<string, string>) => Promise<void>;
  onError: (e: unknown) => void;
  /** data-testid prefix: `<base>-open`, `<base>-input-<key>`, `<base>-add`, `<base>-cancel`,
   *  and with `edit`: `<base>-edit-open`. */
  testIdBase: string;
  /**
   * How the submit button is drawn. Default `"primary"` — unchanged everywhere.
   *
   * review-1 F-3: on a surface that already commits from an `ActionBar` primary, a filled
   * `Add` is a SECOND fill (rule 7), and on the Wizard's mask step it also left the three
   * inline-create rows disagreeing with each other — WS-Z2 demoted the hand-rolled
   * `new-style-add` beside it to a plain `.btn`. `"plain"` makes this row agree. It is a
   * per-call-site choice, not a change to the primitive: `templates/TemplateEditor.tsx`
   * passes nothing and keeps the fill it has today.
   */
  submitVariant?: "primary" | "plain";
  /** Edit-in-place for the currently selected entry; null/undefined hides the trigger. */
  edit?: {
    values: Record<string, string>;
    onSubmit: (values: Record<string, string>) => Promise<void>;
  } | null;
  /** The select (or other control) the triggers sit beside while closed. */
  children: React.ReactNode;
}

export function InlineCreateRow({
  label, fields, onSubmit, onError, testIdBase, edit, submitVariant = "primary", children,
}: InlineCreateRowProps) {
  const [mode, setMode] = useState<"create" | "edit" | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const trimmed = Object.fromEntries(fields.map((f) => [f.key, (values[f.key] ?? "").trim()]));
  const complete = fields.every((f) => trimmed[f.key]);

  const close = () => { setMode(null); setValues({}); };
  const submit = async () => {
    if (!complete || busy) return;
    setBusy(true);
    try {
      await (mode === "edit" ? edit!.onSubmit(trimmed) : onSubmit(trimmed));
      close();
    } catch (e) {
      onError(e); // keep the row open — the typed values survive a failed submit
    } finally {
      setBusy(false);
    }
  };

  if (mode === null) {
    return (
      <div className="inline-create inline-create-row">
        {children}
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          onClick={() => setMode("create")}
          title={label}
          data-testid={`${testIdBase}-open`}
        >
          <Icon name="plus" size={16} /> New
        </button>
        {edit && (
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => { setValues(edit.values); setMode("edit"); }}
            title="Edit the selected entry"
            data-testid={`${testIdBase}-edit-open`}
          >
            Edit
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="new-style" role="group" aria-label={mode === "edit" ? `Edit — ${label}` : label}>
      {fields.map((f, i) => {
        const len = (values[f.key] ?? "").length;
        const showCount = f.maxLength !== undefined && len >= f.maxLength - COUNTER_WITHIN;
        // A Fragment, not a wrapper element: `.new-style` is the flex row and
        // `.new-style__input` is its `flex: 1` child — nesting the input would break both.
        return (
          <Fragment key={f.key}>
            <input
              className="input new-style__input"
              type="text"
              autoFocus={i === 0}
              value={values[f.key] ?? ""}
              placeholder={f.placeholder}
              aria-label={f.ariaLabel}
              maxLength={f.maxLength}
              onChange={(e) => setValues((v) => ({
                ...v,
                [f.key]: f.maxLength === undefined ? e.target.value : e.target.value.slice(0, f.maxLength),
              }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); void submit(); }
                else if (e.key === "Escape") { e.preventDefault(); close(); }
              }}
              data-testid={`${testIdBase}-input-${f.key}`}
            />
            {showCount && (
              <span
                className="faint inline-create-row__hint"
                role="status"
                data-testid={`${testIdBase}-count-${f.key}`}
              >
                {len}/{f.maxLength}
              </span>
            )}
          </Fragment>
        );
      })}
      <button
        type="button"
        className={`btn btn--sm${submitVariant === "primary" ? " btn--primary" : ""}`}
        onClick={() => void submit()}
        disabled={!complete || busy}
        data-testid={`${testIdBase}-add`}
      >
        {busy ? "Saving…" : mode === "edit" ? "Save" : "Add"}
      </button>
      <button type="button" className="btn btn--sm btn--ghost" onClick={close} data-testid={`${testIdBase}-cancel`}>
        Cancel
      </button>
    </div>
  );
}
